<script setup lang="ts">
import { computed } from 'vue';
import Icon from '../icon/Icon.vue';
import type { IconName } from '../icon/Icon.types.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'light' | 'critical';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ButtonProps {
  variant?: ButtonVariant;
  type?: 'button' | 'submit' | 'reset';
  size?: ButtonSize;
  radius?: ButtonRadius;
  loading?: boolean;
  icon?: IconName;
  iconRight?: IconName;
  fullWidth?: boolean;
  subtle?: boolean;
}

const props = withDefaults(defineProps<ButtonProps>(), {
  variant: 'primary',
  size: 'md',
  radius: 'sm',
  type: 'button',
  loading: false,
  fullWidth: false,
  subtle: false,
});

defineEmits<{
  (event: 'click', payload: MouseEvent): void;
}>();

// Statically-mapped class names so Tailwind's source scanner can pick them up.
const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary hover:bg-primary-hover active:bg-primary-active',
  secondary: 'bg-secondary text-on-secondary hover:bg-secondary-hover active:bg-secondary-active',
  ghost:
    'border border-primary text-primary hover:bg-primary-hover/10 hover:border-transparent active:bg-primary-active active:text-white',
  outline: 'border border-border-subtle',
  light: 'bg-primary-light text-primary',
  critical: 'bg-critical text-white hover:bg-critical-hover active:bg-critical-active',
};

const subtleVariantClasses: Record<ButtonVariant, string> = {
  ...variantClasses,
  critical: 'bg-critical/10 text-critical hover:bg-critical/20 active:bg-critical/30',
};

const classes = computed(() => [
  'ui-button',
  'inline-flex items-center justify-center gap-2 px-3 py-2 cursor-pointer',
  'font-medium select-none',
  'transition-colors duration-150',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:opacity-50 disabled:cursor-not-allowed',
  props.subtle ? subtleVariantClasses[props.variant] : variantClasses[props.variant],
]);

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-sm py-xs text-label gap-xs',
  md: 'px-md py-sm text-body gap-sm',
  lg: 'px-lg py-md text-body gap-sm',
};

const radiusClasses: Record<ButtonRadius, string> = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  full: 'rounded-full',
};
</script>

<template>
  <button
    :type="type"
    :class="[classes, radiusClasses[radius], sizeClasses[size], fullWidth && 'w-full']"
    :aria-busy="loading || undefined"
    @click="(event) => $emit('click', event)"
  >
    <span
      v-if="loading"
      class="ui-button-spinner inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
      aria-hidden="true"
    />
    <Icon v-if="icon && !loading" :name="icon" :size="size === 'sm' ? 'sm' : 'md'" />
    <slot />
    <Icon v-if="iconRight && !loading" :name="iconRight" :size="size === 'sm' ? 'sm' : 'md'" />
  </button>
</template>
